/**
 * Moving a whole scaffold with a gizmo.
 *
 * With the Move tool, clicking a part selects the connected scaffold and puts
 * a translate gizmo at its anchor standard — the one with the lowest
 * coordinate key. Dragging the arrows moves everything connected, over the
 * ground only; the anchor snaps through `snapXZ` to the scaffolds standing
 * still, so dropping one run beside another lands it on the module grid, and
 * the gizmo is written back to the snapped spot so it shows where the
 * scaffold actually is.
 */

import { type Camera, Object3D, type Scene } from "three";
import {
  groupFromPoints,
  groupHasPart,
  groupLegs,
  type ScaffoldGroup,
  translateGroup,
} from "../model/connectivity.ts";
import { kXZ } from "../model/geometry.ts";
import { snapXZ } from "../model/solver.ts";
import type { Leg, PartRef, ScaffoldModel } from "../model/types.ts";
import type { CameraRig } from "./cameraRig.ts";
import { Gizmo } from "./gizmo.ts";

/** What the mover needs from the engine. */
export type MoverHost = Readonly<{
  model: ScaffoldModel;
  snapEnabled: () => boolean;
  groundY: (x: number, z: number) => number;
  snapshot: () => void;
  rebuild: () => void;
  hud: (text: string, sticky?: boolean) => void;
  /** The selection changed, or the scaffold moved under it — repaint. */
  onSelectionChange: () => void;
}>;

/** The gizmo sits this far above the anchor standard's foot, metres. */
const ANCHOR_LIFT = 0.6;

type Selection = Readonly<{
  group: ScaffoldGroup;
  /** Live references — the leg map is re-keyed on every step. */
  legs: Leg[];
  anchor: Leg;
}>;

type Drag = { startX: number; startZ: number; appliedX: number; appliedZ: number; changed: boolean };

export class ScaffoldMover {
  private readonly anchorObject = new Object3D();
  private readonly host: MoverHost;
  private gizmo: Gizmo | null = null;
  private scene: Scene | null = null;
  private selected: Selection | null = null;
  private drag: Drag | null = null;
  private active = false;

  constructor(host: MoverHost) {
    this.host = host;
  }

  get selection(): ScaffoldGroup | null {
    return this.selected?.group ?? null;
  }

  /**
   * Is this part of the selected scaffold? Standards are checked by their live
   * coordinates, because the group's keys go stale while a drag re-keys them.
   */
  isSelectedPart(ref: PartRef): boolean {
    const selected = this.selected;
    if (!selected) {
      return false;
    }
    if (ref.type === "leg") {
      return selected.legs.some((leg) => kXZ(leg.x, leg.z) === ref.key);
    }

    return groupHasPart(selected.group, ref);
  }

  attach(scene: Scene, camera: Camera, domElement: HTMLElement, rig: CameraRig): void {
    this.scene = scene;
    scene.add(this.anchorObject);
    this.gizmo = new Gizmo(scene, camera, domElement, rig, {
      dragStart: () => this.beginDrag(),
      change: () => this.onChange(),
      dragEnd: () => this.endDrag(),
    });
    this.gizmo.controls.setMode("translate");
    this.gizmo.controls.showY = false;
    this.gizmo.setActive(this.active);
    this.placeAnchor();
  }

  detach(): void {
    this.gizmo?.dispose();
    this.gizmo = null;
    this.scene?.remove(this.anchorObject);
    this.scene = null;
  }

  /** The gizmo only answers the pointer while the Move tool is active. */
  setActive(active: boolean): void {
    this.active = active;
    this.gizmo?.setActive(active);
  }

  isPointerOnGizmo(): boolean {
    return this.gizmo?.isPointerOn() ?? false;
  }

  wasPressOnGizmo(): boolean {
    return this.gizmo?.wasPressOn() ?? false;
  }

  /** Select a scaffold (or nothing) and park the gizmo on its anchor standard. */
  select(group: ScaffoldGroup | null): void {
    const legs = group ? groupLegs(this.host.model, group) : [];
    const anchorKey = group ? [...group.legKeys].sort()[0] : undefined;
    const anchor = anchorKey === undefined ? undefined : this.host.model.legs.get(anchorKey);

    this.selected = group && anchor ? { group, legs, anchor } : null;
    this.placeAnchor();
    this.host.onSelectionChange();
  }

  private placeAnchor(): void {
    const anchor = this.selected?.anchor;
    if (anchor) {
      this.anchorObject.position.set(anchor.x, this.host.groundY(anchor.x, anchor.z) + ANCHOR_LIFT, anchor.z);
    }
    this.gizmo?.attach(anchor ? this.anchorObject : null);
  }

  /** After a move the keys and indices have changed: rebuild the group from the anchor's new place. */
  private reselect(): void {
    const anchor = this.selected?.anchor;
    if (!anchor) {
      return;
    }

    this.select(groupFromPoints(this.host.model, [{ x: anchor.x, z: anchor.z }]));
  }

  private beginDrag(): void {
    const selected = this.selected;
    if (!selected) {
      return;
    }

    this.host.snapshot();
    this.drag = {
      startX: selected.anchor.x,
      startZ: selected.anchor.z,
      appliedX: 0,
      appliedZ: 0,
      changed: false,
    };
    this.host.hud(`Moving ${selected.legs.length} standards — release to drop`, true);
  }

  /**
   * Apply the gizmo's travel to the scaffold. The anchor is snapped against the
   * standards that are NOT moving, and the gizmo is put where the snap landed.
   */
  private onChange(): void {
    const drag = this.drag;
    const selected = this.selected;
    if (!drag || !selected) {
      return;
    }

    const raw = this.anchorObject.position;
    const moving = new Set(selected.legs.map((leg) => kXZ(leg.x, leg.z)));
    const target = snapXZ(this.host.model, raw.x, raw.z, this.host.snapEnabled(), moving);
    const stepX = +(target.x - (drag.startX + drag.appliedX)).toFixed(3);
    const stepZ = +(target.z - (drag.startZ + drag.appliedZ)).toFixed(3);
    raw.set(target.x, raw.y, target.z);
    if (stepX === 0 && stepZ === 0) {
      return;
    }

    translateGroup(this.host.model, selected.group, selected.legs, stepX, stepZ);
    drag.appliedX += stepX;
    drag.appliedZ += stepZ;
    drag.changed = true;
    this.host.rebuild();

    const east = (drag.appliedX * 1000).toFixed(0);
    const north = (drag.appliedZ * 1000).toFixed(0);
    this.host.hud(`Moved ${east} mm × ${north} mm — release to drop`, true);
  }

  private endDrag(): void {
    const drag = this.drag;
    const selected = this.selected;
    this.drag = null;
    if (!drag || !selected) {
      return;
    }

    if (drag.changed) {
      // Resolve coincident standards for real now the drop position is final.
      const { merged } = translateGroup(this.host.model, selected.group, selected.legs, 0, 0, true);
      this.host.rebuild();
      const note = merged > 0 ? ` · ${merged} merged into a standing standard` : "";
      this.host.hud(`Moved ${selected.legs.length} standards${note}`);
    }
    this.reselect();
  }
}
